import React, { useEffect, useState } from 'react'
import { Text } from '@chakra-ui/react'
import { useRentalSearchQuery } from 'graphql/generated'

import { SearchDropdown } from 'components/ChakraToolkit/SearchDropdown'
import { formatUTCDate } from 'lib/date-time'
import { buildRentalSearchQueryArgs } from './rentalSearchQuery'

type RentalSearchResult = {
  id: string
  address: string
  users: string
  occupancyRange: string
}

const formatSearchDate = (date: string | null | undefined) =>
  date ? formatUTCDate(date, 'MMM d, yyyy') : 'Unknown'

const getOccupancyRange = (
  occupancyDate: string | null | undefined,
  moveOutDate: string | null | undefined
) =>
  `${formatSearchDate(occupancyDate)} - ${moveOutDate ? formatSearchDate(moveOutDate) : 'Present'}`

const parseSearchResult = (result: string): RentalSearchResult => JSON.parse(result)

export default function RentalSearch() {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchInput), 300)
    return () => clearTimeout(timeout)
  }, [searchInput])

  const searchQueryArgs = buildRentalSearchQueryArgs(debouncedSearch)

  const [{ data, fetching }] = useRentalSearchQuery({
    ...searchQueryArgs,
    requestPolicy: 'network-only',
    context: React.useMemo(() => ({ suspense: false }), [])
  })

  const isInSync = debouncedSearch === searchInput
  const rentalData =
    !searchQueryArgs.pause && isInSync && !fetching && data?.rentals
      ? data.rentals.map((rental) => {
          const users = rental.rental_users
            .map((rental_user) =>
              [rental_user.user.first_name, rental_user.user.last_name].filter(Boolean).join(' ')
            )
            .filter(Boolean)
            .join(', ')
          const address = [
            rental.portfolio_home?.home.address.display_line_1,
            rental.portfolio_home?.home.address.display_line_2
          ]
            .filter(Boolean)
            .join(', ')

          return JSON.stringify({
            id: rental.id,
            address,
            users,
            occupancyRange: getOccupancyRange(rental.occupancy_date, rental.move_out_date)
          } satisfies RentalSearchResult)
        })
      : []

  return (
    <SearchDropdown
      data={rentalData}
      placeholder='Search for a rental...'
      strictlyUsePlaceholder={true}
      onSearchChange={setSearchInput}
      minSearchLength={2}
      formatDropdownItem={(result: string) => {
        const { address, users, occupancyRange } = parseSearchResult(result)
        return (
          <>
            <Text fontWeight='semibold'>{address}</Text>
            <Text color='gray.700'>{users}</Text>
            <Text color='gray.500' fontSize='sm'>
              {occupancyRange}
            </Text>
          </>
        )
      }}
      formatSelectedItem={(result: string) => {
        const { address } = parseSearchResult(result)
        return address
      }}
      action={(result: string) => {
        const { id } = parseSearchResult(result)
        window.open(`/rent-roll/${id}`, '_blank')
      }}
    />
  )
}
